// Signer store — key management for the mockup.
//
// Two modes coexist; both hold a chipnet WIF in localStorage and sign txs
// directly (bypassing WizardConnect). Semantically distinct so the UI can
// treat them differently:
//
//   1. DEV mode — localhost-only, LS key `deviant.potidaea.dev-wif`
//      Single-slot automation lane: pinned by a developer for smoke tests;
//      the deployed URL cannot activate it (see isLocalhost gate).
//
//   2. USER mode — any host, LS key `deviant.potidaea.wallets.v1`
//      Multi-wallet user-visible store surfaced by the wallet menu. Each
//      wallet is `{ wif, label }`; one is `activeWif`. Users can create /
//      import multiple keys and switch between them without losing the
//      others. Removing the active wallet auto-selects another (or clears
//      the active pointer if the list becomes empty).
//
// USER takes precedence over DEV takes precedence over WizardConnect (see
// main.ts). Both use the same Signer shape; downstream code doesn't care
// which mode it came from.
//
// SAFETY POSTURE FOR USER MODE:
//   - Chipnet only. Address prefix hard-coded to bchtest.
//   - Every stored WIF stays in this browser's localStorage. No network
//     sync, no upload path.
//   - No backup / no recovery. The Create modal shows the WIF once at
//     generation for copy-out. Losing browser storage = losing all keys.
//   - Not for mainnet. The mockup itself lives on chipnet endpoints.
//
// The dev-mode single-slot design is preserved so existing automated
// smoke tests keep working with their pinned WIF; the user-mode multi-
// slot store is separate on purpose so a developer can flip between
// "smoke rig" and "user flow" without collision.

import {
  decodePrivateKeyWif,
  encodePrivateKeyWif,
  secp256k1,
  sha256,
  ripemd160,
  encodeCashAddress,
  generatePrivateKey,
  CashAddressType,
  CashAddressNetworkPrefix,
} from '@bitauth/libauth';

export type SignerMode = 'dev' | 'user';

export type Signer = {
  mode: SignerMode;
  privateKey: Uint8Array;
  publicKey: Uint8Array;         // compressed
  publicKeyHash: Uint8Array;     // 20 bytes
  cashaddr: string;              // bchtest: P2PKH
  tokenaddr: string;             // bchtest: token-aware
};

/** Public view of a stored user wallet. WIF is deliberately NOT in the
 *  shape — the menu just needs address + label to render + switch. Callers
 *  that need to sign go through loadActiveSigner() / switchUserWallet(). */
export type UserWallet = {
  label: string;
  cashaddr: string;
  isActive: boolean;
};

const LS_KEY_DEV = 'deviant.potidaea.dev-wif';
const LS_KEY_USERS = 'deviant.potidaea.wallets.v1';
const LS_KEY_LEGACY_USER_WIF = 'deviant.potidaea.wallet-wif';   // pre-multi-wallet

type StoredEntry = { wif: string; label: string };
type StoredShape = { wallets: StoredEntry[]; activeWif: string | null };

function isLocalhost(): boolean {
  const h = location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '';
}

function encAddr(pkh: Uint8Array, type: CashAddressType): string {
  const res = encodeCashAddress({ prefix: CashAddressNetworkPrefix.testnet, type, payload: pkh });
  if (typeof res === 'string') return res;
  // @ts-expect-error runtime shape check
  return res.address ?? String(res);
}

/** Build a Signer from a WIF string. Returns null on decode failure. */
export function signerFromWif(wif: string, mode: SignerMode): Signer | null {
  const dec = decodePrivateKeyWif(wif.trim());
  if (typeof dec === 'string') { console.warn('[signer] WIF decode failed:', dec); return null; }
  const privateKey = dec.privateKey;
  const publicKey = secp256k1.derivePublicKeyCompressed(privateKey);
  if (typeof publicKey === 'string') { console.warn('[signer] pk derive failed:', publicKey); return null; }
  const publicKeyHash = ripemd160.hash(sha256.hash(publicKey));
  return {
    mode,
    privateKey,
    publicKey,
    publicKeyHash,
    cashaddr: encAddr(publicKeyHash, CashAddressType.p2pkh),
    tokenaddr: encAddr(publicKeyHash, CashAddressType.p2pkhWithTokens),
  };
}

// ── USER-mode multi-wallet store ────────────────────────────────────────────

function readStore(): StoredShape {
  // Migrate from the legacy single-slot key if present. One-shot: after
  // migration we delete the legacy key so we don't do it again.
  let legacy: string | null = null;
  try { legacy = localStorage.getItem(LS_KEY_LEGACY_USER_WIF); } catch { /* private mode */ }
  if (legacy) {
    let existing: StoredShape | null = null;
    try {
      const raw = localStorage.getItem(LS_KEY_USERS);
      if (raw) existing = JSON.parse(raw) as StoredShape;
    } catch { /* corrupt */ }
    if (!existing || !existing.wallets?.some((w) => w.wif === legacy)) {
      const migrated: StoredShape = {
        wallets: [...(existing?.wallets ?? []), { wif: legacy, label: labelForNew(existing?.wallets ?? []) }],
        activeWif: existing?.activeWif ?? legacy,
      };
      try {
        localStorage.setItem(LS_KEY_USERS, JSON.stringify(migrated));
        localStorage.removeItem(LS_KEY_LEGACY_USER_WIF);
      } catch { /* noop */ }
      return migrated;
    } else {
      // legacy key was a duplicate — just drop it
      try { localStorage.removeItem(LS_KEY_LEGACY_USER_WIF); } catch { /* noop */ }
    }
  }
  try {
    const raw = localStorage.getItem(LS_KEY_USERS);
    if (!raw) return { wallets: [], activeWif: null };
    const parsed = JSON.parse(raw) as StoredShape;
    // sanity-guard fields so a hand-edited store can't crash the app
    if (!Array.isArray(parsed.wallets)) return { wallets: [], activeWif: null };
    return { wallets: parsed.wallets, activeWif: parsed.activeWif ?? parsed.wallets[0]?.wif ?? null };
  } catch { return { wallets: [], activeWif: null }; }
}

function writeStore(s: StoredShape): void {
  try { localStorage.setItem(LS_KEY_USERS, JSON.stringify(s)); } catch { /* private mode */ }
}

function labelForNew(existing: StoredEntry[]): string {
  // "Wallet 1", "Wallet 2", … — pick the lowest unused positive integer.
  const used = new Set(existing.map((w) => w.label));
  for (let i = 1; i < 1000; i++) {
    const cand = `Wallet ${i}`;
    if (!used.has(cand)) return cand;
  }
  return `Wallet ${Date.now()}`;
}

/** Return the metadata for every stored user wallet, plus which one is
 *  currently active. Order is insertion-order (stable across reloads). */
export function listUserWallets(): UserWallet[] {
  const store = readStore();
  return store.wallets.map((w) => {
    const s = signerFromWif(w.wif, 'user');
    return {
      label: w.label,
      cashaddr: s?.cashaddr ?? '(invalid)',
      isActive: store.activeWif === w.wif,
    };
  });
}

/** Load whichever signer is active. USER wins over DEV. */
export function loadActiveSigner(): Signer | null {
  const store = readStore();
  if (store.activeWif) {
    const s = signerFromWif(store.activeWif, 'user');
    if (s) return s;
    // active WIF is corrupt — drop it, fall through
    const rest = store.wallets.filter((w) => w.wif !== store.activeWif);
    writeStore({ wallets: rest, activeWif: rest[0]?.wif ?? null });
  }
  // No active user wallet — fall through to DEV
  if (isLocalhost()) {
    let devWif: string | null = null;
    try { devWif = localStorage.getItem(LS_KEY_DEV); } catch { /* private mode */ }
    if (devWif) return signerFromWif(devWif, 'dev');
  }
  return null;
}

/** Import a WIF as a new user wallet. Duplicates are folded — if the WIF
 *  is already stored we just make it active. Returns the Signer or null on
 *  WIF decode failure. */
export function importUserWif(wif: string, label?: string): Signer | null {
  const trimmed = wif.trim();
  const s = signerFromWif(trimmed, 'user');
  if (!s) return null;
  const store = readStore();
  const existing = store.wallets.find((w) => w.wif === trimmed);
  if (existing) {
    writeStore({ wallets: store.wallets, activeWif: trimmed });
  } else {
    writeStore({
      wallets: [...store.wallets, { wif: trimmed, label: label ?? labelForNew(store.wallets) }],
      activeWif: trimmed,
    });
  }
  return s;
}

/** Change the display label of the wallet with `cashaddr`. Returns true on
 *  success, false if no wallet with that address is stored. Trims + clamps
 *  the label to 32 bytes so a malformed input can't grow the menu row. */
export function renameUserWallet(cashaddr: string, newLabel: string): boolean {
  const cleaned = newLabel.trim().slice(0, 32);
  if (!cleaned) return false;
  const store = readStore();
  let hit = false;
  const wallets = store.wallets.map((w) => {
    const s = signerFromWif(w.wif, 'user');
    if (s && s.cashaddr === cashaddr) { hit = true; return { ...w, label: cleaned }; }
    return w;
  });
  if (!hit) return false;
  writeStore({ wallets, activeWif: store.activeWif });
  return true;
}

/** Generate a fresh chipnet key + WIF, append + activate, return both. The
 *  Create modal shows the WIF once for copy-out — the only backup chance. */
export function createUserWallet(label?: string): { signer: Signer; wif: string; label: string } | null {
  const privateKey = generatePrivateKey();
  const wif = encodePrivateKeyWif(privateKey, 'testnet');
  const s = signerFromWif(wif, 'user');
  if (!s) return null;
  const store = readStore();
  const chosenLabel = label ?? labelForNew(store.wallets);
  writeStore({
    wallets: [...store.wallets, { wif, label: chosenLabel }],
    activeWif: wif,
  });
  return { signer: s, wif, label: chosenLabel };
}

/** Make the wallet with `cashaddr` active. Returns its Signer, or null if
 *  no stored wallet matches that address. */
export function switchUserWallet(cashaddr: string): Signer | null {
  const store = readStore();
  for (const w of store.wallets) {
    const s = signerFromWif(w.wif, 'user');
    if (s && s.cashaddr === cashaddr) {
      writeStore({ wallets: store.wallets, activeWif: w.wif });
      return s;
    }
  }
  return null;
}

/** Remove the wallet with `cashaddr` from storage. If it was active, the
 *  next wallet in the list becomes active (or the store empties). Returns
 *  the new active Signer, or null if the store is now empty. */
export function removeUserWallet(cashaddr: string): Signer | null {
  const store = readStore();
  const idx = store.wallets.findIndex((w) => {
    const s = signerFromWif(w.wif, 'user'); return s?.cashaddr === cashaddr;
  });
  if (idx < 0) return loadActiveSigner();   // wasn't there — no state change
  const removed = store.wallets[idx];
  const remaining = store.wallets.filter((_, i) => i !== idx);
  const wasActive = removed.wif === store.activeWif;
  const nextActive = wasActive ? (remaining[0]?.wif ?? null) : store.activeWif;
  writeStore({ wallets: remaining, activeWif: nextActive });
  if (nextActive) {
    const s = signerFromWif(nextActive, 'user');
    if (s) return s;
  }
  return null;
}

/** Remove ALL user wallets. Used by "detach current" when the user wants a
 *  clean slate; kept for parity with the old single-slot clear API. */
export function clearUserWallet(): void {
  writeStore({ wallets: [], activeWif: null });
}

/** Read the currently-active WIF back (for a "reveal WIF" flow — only ever
 *  exposed through explicit user action, never logged). */
export function readUserWif(): string | null {
  const store = readStore();
  return store.activeWif;
}

// ── DEV-mode operations (localhost only) ────────────────────────────────────

export function pinDevWif(wif: string): void {
  if (!isLocalhost()) { console.warn('[signer] non-localhost — refusing to pin dev WIF'); return; }
  try { localStorage.setItem(LS_KEY_DEV, wif); } catch { /* private mode */ }
  console.log('[signer] dev WIF pinned — reload to activate');
}

export function clearDevWif(): void {
  try { localStorage.removeItem(LS_KEY_DEV); } catch { /* noop */ }
  console.log('[signer] dev WIF cleared — reload to deactivate');
}
